import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import type React from "react";
import { useRef, useState } from "react";

import useQueryClient from "../../../api/query";
import { ErrorHelperText } from "../../PatientsDashboard/ui/ErrorHelper";
import {
  extractOtpauthSecret,
  isPlausibleSetupKey,
  normalizeSetupKey,
} from "../common";
import { decodeQrSecret } from "../qrDecode";
import { TotpCode } from "../ui/TotpCode";

const inputSx = {
  backgroundColor: "#F9FAFB",
  width: "100%",
  fontSize: "1rem",
  ".MuiInputBase-root": {
    borderRadius: "8px",
    border: "none",
  },
  ".MuiOutlinedInput-notchedOutline": {
    border: "solid 1px rgba(208, 213, 221, 0.5)",
  },
} as const;

interface AuthenticatorSetupKeyFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
  name?: string;
  error?: string;
}

export function AuthenticatorSetupKeyField({
  value,
  onChange,
  onBlur,
  inputRef = undefined,
  name = undefined,
  error = undefined,
}: AuthenticatorSetupKeyFieldProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [setupKeyError, setSetupKeyError] = useState<string | null>(null);

  const fetchTotpPreview = async () => {
    try {
      const code = await queryClient.postTotpPreview(
        normalizeSetupKey(value ?? ""),
      );
      setSetupKeyError(null);
      return code;
    } catch (err) {
      setSetupKeyError("Invalid authenticator setup key");
      throw err;
    }
  };

  const handleQrFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers onChange.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (!file) {
      return;
    }
    try {
      const secret = await decodeQrSecret(file);
      setSetupKeyError(null);
      onChange(normalizeSetupKey(secret));
    } catch (err) {
      setSetupKeyError(
        err instanceof Error ? err.message : "Could not read QR code.",
      );
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: "0.5rem", width: "100%" }}>
        <TextField
          name={name}
          value={value ?? ""}
          type="text"
          variant="outlined"
          placeholder="Enter or paste setup key / otpauth:// URI"
          inputRef={inputRef}
          onChange={(e) => {
            const next = e.target.value;
            setSetupKeyError(null);
            if (next.trim().startsWith("otpauth://")) {
              const secret = extractOtpauthSecret(next);
              onChange(secret ? normalizeSetupKey(secret) : next);
            } else {
              onChange(next);
            }
          }}
          onBlur={() => {
            if (value) {
              onChange(normalizeSetupKey(value));
            }
            onBlur();
          }}
          inputProps={{
            "data-testid": "mfaSetupKeyInput",
            autoComplete: "off",
          }}
          sx={{ ...inputSx, flex: 1 }}
        />
        <Button
          variant="outlined"
          startIcon={<QrCodeScannerOutlinedIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            borderColor: "rgba(208, 213, 221, 0.5)",
            color: "#374151",
          }}
        >
          Upload QR
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          data-testid="mfaQrInput"
          onChange={handleQrFile}
        />
      </Box>
      <ErrorHelperText message={error ?? setupKeyError ?? undefined} />
      {isPlausibleSetupKey(value) && (
        <Box sx={{ marginTop: "0.5rem", width: "100%" }}>
          <TotpCode
            key={normalizeSetupKey(value ?? "")}
            fetchCode={fetchTotpPreview}
          />
        </Box>
      )}
    </>
  );
}

AuthenticatorSetupKeyField.defaultProps = {
  inputRef: undefined,
  name: undefined,
  error: undefined,
};
